import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2, Pause, Play, AlertTriangle, X } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import './control-tower.css';

/**
 * The Fleet Control Tower, inside the hub.
 *
 * Same wall display, but a page of this website rather than a separate address:
 * you are already signed in, so there is no second login, and the Fullscreen
 * button turns this tab into the TV.
 *
 * Data is polled through the hub's own API, which reaches the tower server-side.
 * Polling rather than a socket because the browser cannot put a bearer token on
 * a WebSocket handshake, and the tower only produces a new reading every 15
 * seconds anyway - a socket would deliver the same picture no sooner.
 */

const POLL_MS = 10_000;
const REGION_DWELL_MS = 17_000;
const OVERVIEW_DWELL_MS = 12_000;
const ALERT_HOLD_MS = 20_000;
const TZ = 'Asia/Riyadh';

interface Unit {
  unitCode: string; plate: string; kind: string;
  lat: number; lng: number; speedKph: number;
  status: 'moving' | 'idle' | 'stopped' | 'offline';
  region: string | null;
  driverName: string | null; driverPhone: string | null;
  loadKg: number | null; fuelPct: number | null;
  odometerKm: number | null; engineHours: number | null;
  kmToday: number; minutesSinceReport: number;
}
interface Region { key: string; name: string; lat: number; lng: number; zoom: number; tag: string }
interface Alert { id: number; unit_code: string; severity: string; message: string; opened_at: string; data?: { lat?: number; lng?: number; driver?: string; phone?: string } }
interface Ev { id: number; message: string; severity: string; occurred_at: string }

interface TowerState {
  available: boolean; reason?: string;
  source: string | null; simulated: boolean; stale: boolean; generatedAt: string | null;
  units: Unit[]; regions: Region[];
  ksaView: { lat: number; lng: number; zoom: number } | null;
  counts: { moving: number; idle: number; stopped: number; offline: number };
  fleetKmToday: number;
  alerts: Alert[]; events: Ev[];
  fuel: Fuel | null;
  untracked: Untracked[];
}

/** An active truck no tracker reports on. It cannot be mapped, but it must not
 *  be invisible either - the fleet is 72 vehicles, not the 25 with a device. */
interface Untracked {
  code: string; plate: string; kind: string | null;
  driverName: string | null; driverPhone: string | null;
  lastOdometerKm: number | null; lastFuelAt: string | null; region: string | null;
}

/** Fuel as bought on the cards - the trackers carry no fuel sensor. */
interface Fuel {
  byUnit: Record<string, { liters: number; sar: number; fills: number; litersPer100Km: number | null }>;
  today: { liters: number; sar: number; byProvider: ProviderTotal[] };
  period: { days: number; liters: number; sar: number; byProvider: ProviderTotal[] };
  unmatched: { count: number; sar: number };
}
interface ProviderTotal { provider: string; liters: number; sar: number; fills: number }

const PROVIDER_LABEL: Record<string, string> = {
  petroapp: 'PetroApp', fleetplus: 'Fleet Plus', aldrees: 'Aldrees', cash: 'Cash', other: 'Other',
};

const EMPTY: TowerState = {
  available: false, source: null, simulated: false, stale: false, generatedAt: null,
  units: [], regions: [], ksaView: null,
  counts: { moving: 0, idle: 0, stopped: 0, offline: 0 }, fleetKmToday: 0,
  alerts: [], events: [], fuel: null, untracked: [],
};

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
const hhmm = (iso: string) => new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));

export default function ControlTowerPage() {
  const [s, setS] = useState<TowerState>(EMPTY);
  const [failed, setFailed] = useState(false);
  const [paused, setPaused] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [regionIdx, setRegionIdx] = useState<number | null>(null);
  const [openAlert, setOpenAlert] = useState<Alert | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markers = useRef(new Map<string, L.Marker>());
  const shownAlerts = useRef(new Set<number>());
  const clock = useTicking();

  /* ------------------------------------------------------------- data */

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiClient.get<TowerState>('/api/v1/fleet-tower/state')
        .then((r) => { if (!cancelled) { setS(r.data); setFailed(!r.data.available); } })
        // Keep the last picture on screen rather than blanking the wall.
        .catch(() => { if (!cancelled) setFailed(true); });
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  /* -------------------------------------------------------------- map */

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, {
      zoomControl: false, attributionControl: true,
      keyboard: false, boxZoom: false, doubleClickZoom: false,
    }).setView([23.9, 45.1], 5.4);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18, attribution: '© OpenStreetMap',
    }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; markers.current.clear(); };
  }, []);

  const activeRegion = regionIdx != null ? s.regions[regionIdx] : undefined;

  // Markers are moved and re-iconed, never rebuilt: this page runs for days.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Every truck stays on the map, always. Drawing only the region the camera
    // happens to be over made the rest of the fleet disappear - and on a fleet
    // this size that left two or three markers on screen. The camera still
    // tours; the trucks it is not looking at remain visible around it.
    const visible = s.units;
    const wanted = new Set<string>();

    for (const u of visible) {
      wanted.add(u.unitCode);
      const html =
        `<div class="ct-truck ${u.status}">` +
        (u.loadKg != null
          ? `<div class="ct-load"><i style="width:${Math.max(0, Math.min(100, (u.loadKg / 24000) * 100))}%"></i></div>` : '') +
        `<div class="ct-body"></div><div class="ct-label">${u.unitCode}</div></div>`;
      const icon = L.divIcon({ className: '', html, iconSize: [0, 0] });
      const existing = markers.current.get(u.unitCode);
      if (existing) {
        existing.setLatLng([u.lat, u.lng]);
        existing.setIcon(icon);
      } else {
        const m = L.marker([u.lat, u.lng], { icon, keyboard: false }).addTo(map);
        m.on('click', () => { setSelected(u.unitCode); setPaused(true); });
        markers.current.set(u.unitCode, m);
      }
    }
    for (const [code, m] of markers.current) {
      if (!wanted.has(code)) { m.remove(); markers.current.delete(code); }
    }
  }, [s.units]);

  /* ------------------------------------------------------------- tour */

  useEffect(() => {
    if (paused || openAlert || s.regions.length === 0) return;
    const stops = s.regions.length + 1;
    const next = () => setRegionIdx((i) => {
      const cur = i == null ? -1 : i;
      const step = (cur + 1) % stops;
      return step === s.regions.length ? null : step;
    });
    const dwell = regionIdx == null ? OVERVIEW_DWELL_MS : REGION_DWELL_MS;
    const t = setTimeout(next, dwell);
    return () => clearTimeout(t);
  }, [paused, openAlert, regionIdx, s.regions.length]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (activeRegion) map.flyTo([activeRegion.lat, activeRegion.lng], activeRegion.zoom, { duration: 2.2 });
    else {
      const v = s.ksaView ?? { lat: 23.9, lng: 45.1, zoom: 5.4 };
      map.flyTo([v.lat, v.lng], v.zoom, { duration: 2.2 });
    }
  }, [activeRegion, s.ksaView]);

  /* ------------------------------------------------------- alert override */

  useEffect(() => {
    if (openAlert || paused) return;
    const critical = s.alerts.find((a) => a.severity === 'critical' && !shownAlerts.current.has(a.id));
    if (!critical) return;
    shownAlerts.current.add(critical.id);
    setOpenAlert(critical);
    if (critical.data?.lat && critical.data?.lng) {
      mapRef.current?.flyTo([critical.data.lat, critical.data.lng], 10, { duration: 1.6 });
    }
    const t = setTimeout(() => setOpenAlert(null), ALERT_HOLD_MS);
    return () => clearTimeout(t);
  }, [s.alerts, openAlert, paused]);

  /* -------------------------------------------------------- fullscreen */

  const toggleFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) void el.requestFullscreen?.().then(() => setFullscreen(true)).catch(() => {});
    else void document.exitFullscreen?.().then(() => setFullscreen(false)).catch(() => {});
  }, []);
  useEffect(() => {
    const h = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  /* ----------------------------------------------------------- derived */

  const shown = useMemo(
    () => (activeRegion ? s.units.filter((u) => u.region === activeRegion.key) : s.units),
    [s.units, activeRegion],
  );
  const regionCounts = useMemo(() => {
    const c = { moving: 0, idle: 0, stopped: 0, offline: 0 };
    let km = 0;
    for (const u of shown) { c[u.status]++; km += u.kmToday || 0; }
    return { ...c, km };
  }, [shown]);

  const selectedUnit = selected ? s.units.find((u) => u.unitCode === selected) ?? null : null;
  const unitFuel = selectedUnit && s.fuel ? s.fuel.byUnit[selectedUnit.unitCode] ?? null : null;
  const fuelDays = s.fuel?.period.days ?? 30;
  const ageSeconds = s.generatedAt ? Math.round((clock - new Date(s.generatedAt).getTime()) / 1000) : null;

  return (
    <div className="ct-root">
      <header className="ct-top">
        <div className="ct-brand">
          {!fullscreen && (
            <Link to="/dashboard/fleet/live" className="ct-back" title="Back to Fleet Live">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          )}
          <div className="ct-mark">JASR</div>
          <div>
            <div className="ct-title">FLEET COMMAND</div>
            <div className="ct-sub">
              {failed
                ? 'tower not responding — showing last known'
                : `${s.source ?? '—'}${s.stale ? ' · STALE' : ''}${ageSeconds != null ? ` · updated ${ageSeconds}s ago` : ''}`}
              {s.simulated && <span className="ct-simulated">SIMULATED FLEET</span>}
            </div>
          </div>
        </div>

        <div className="ct-chips">
          <Chip label="MOVING" value={s.counts.moving} />
          <Chip label="IDLE" value={s.counts.idle} />
          <Chip label="STOPPED" value={s.counts.stopped} />
          <Chip label="OFFLINE" value={s.counts.offline} danger={s.counts.offline > 0} />
          {/* The whole fleet, not just the part with a device on it. */}
          <Chip label="NO TRACKER" value={s.untracked.length} warn={s.untracked.length > 0} />
          <Chip label="FLEET KM TODAY" value={fmt(s.fleetKmToday)} wide />
          {s.fuel && <Chip label="FUEL TODAY SAR" value={fmt(s.fuel.today.sar)} wide />}
          {s.fuel && <Chip label={`FUEL ${s.fuel.period.days}D SAR`} value={fmt(s.fuel.period.sar)} wide />}
          <Chip label="ALERTS" value={s.alerts.length} warn={s.alerts.length > 0} />
        </div>

        <div className="ct-right">
          <div className="ct-clock">
            {new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(clock)}
          </div>
          <div className="ct-sub">
            {new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'short', day: '2-digit', month: 'short' }).format(clock)}
          </div>
          <div className="ct-actions">
            <Button size="sm" variant="outline" onClick={() => setPaused((p) => !p)} title={paused ? 'Resume the tour' : 'Pause the tour'}>
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="outline" onClick={toggleFullscreen} title="Fullscreen for the TV">
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="ct-main">
        <section className="ct-stage">
          <div ref={mapEl} className="ct-map" />

          <div className="ct-panel ct-region">
            <div className="ct-regionName">{activeRegion ? activeRegion.name.toUpperCase() : 'FLEET OVERVIEW — ALL KINGDOM'}</div>
            <div className="ct-regionStats">
              <span>TRACKED <b>{shown.length}</b></span>
              <span>MOVING <b>{regionCounts.moving}</b></span>
              <span>IDLE <b>{regionCounts.idle}</b></span>
              <span>STOPPED <b>{regionCounts.stopped}</b></span>
              <span>OFFLINE <b>{regionCounts.offline}</b></span>
              <span>KM TODAY <b>{fmt(regionCounts.km)}</b></span>
            </div>
          </div>

          <div className="ct-panel ct-mini">
            <div className="ct-miniTitle">KINGDOM</div>
            <div className="ct-bubbles">
              {s.regions.map((r) => {
                const list = s.units.filter((u) => u.region === r.key);
                const bad = list.some((u) => u.status === 'offline');
                return (
                  <div key={r.key} className={`ct-bubble ${bad ? 'bad' : ''}`}>
                    <div className="tag">{r.tag}</div>
                    <div className="n">{list.length}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Where the diesel money went. Split by card, because that is how it
              is bought and how it is reconciled. */}
          {s.fuel && s.fuel.period.byProvider.length > 0 && (
            <div className="ct-panel ct-fuel">
              <div className="ct-miniTitle">FUEL · LAST {s.fuel.period.days} DAYS</div>
              {s.fuel.period.byProvider.map((p) => (
                <div className="ct-fuelRow" key={p.provider}>
                  <span className="name">{PROVIDER_LABEL[p.provider] ?? p.provider}</span>
                  <span className="sar">SAR {fmt(p.sar)}</span>
                  <span className="litres">{fmt(p.liters)} L</span>
                </div>
              ))}
              <div className="ct-fuelRow total">
                <span className="name">Total</span>
                <span className="sar">SAR {fmt(s.fuel.period.sar)}</span>
                <span className="litres">{fmt(s.fuel.period.liters)} L</span>
              </div>
              {s.fuel.unmatched.count > 0 && (
                <div className="ct-fuelWarn">
                  {s.fuel.unmatched.count} fill{s.fuel.unmatched.count === 1 ? '' : 's'} not linked to a truck · SAR {fmt(s.fuel.unmatched.sar)}
                </div>
              )}
            </div>
          )}

          <div className="ct-panel ct-scene">
            {paused ? 'TOUR PAUSED' : `LIVE REGIONAL TOUR${activeRegion ? ` · ${activeRegion.tag}` : ' · KINGDOM'}`}
          </div>

          {/* Down the side, not across the middle. An alert that covers the map
              hides the fleet, and the point of the wall is seeing where the
              trucks are while you deal with the one that needs attention. */}
          {openAlert && (
            <div className="ct-alert">
              <div className="sev">{openAlert.severity.toUpperCase()}</div>
              <div className="msg">{openAlert.message}</div>
              <div className="meta">
                {[openAlert.data?.driver, openAlert.data?.phone, `since ${hhmm(openAlert.opened_at)}`].filter(Boolean).join(' · ')}
              </div>
              <div className="action">
                {openAlert.data?.phone ? `Call the driver on ${openAlert.data.phone}` : 'Check the unit and its tracker'}
              </div>
              <button className="ct-alertClose" onClick={() => setOpenAlert(null)} aria-label="Dismiss">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {selectedUnit && (
            <div className="ct-panel ct-truck">
              <div className="head">
                <div>
                  <div className="unit">{selectedUnit.unitCode}</div>
                  <div className="plate">{selectedUnit.plate || '—'} · {selectedUnit.kind} · {selectedUnit.driverName ?? 'no driver assigned'}</div>
                </div>
                <button className="close" onClick={() => { setSelected(null); setPaused(false); }} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid">
                <Cell label="STATUS" value={selectedUnit.status.toUpperCase()} />
                <Cell label="SPEED" value={`${Math.round(selectedUnit.speedKph)} km/h`} />
                <Cell label="KM TODAY" value={fmt(selectedUnit.kmToday)} />
                <Cell label="ODOMETER" value={selectedUnit.odometerKm ? `${fmt(selectedUnit.odometerKm)} km` : '—'} />
                {/* Fuel is what was bought on the cards over the period, not a
                    tank reading - these trucks have no fuel sensor. */}
                <Cell label={`FUEL ${fuelDays}D`} value={unitFuel ? `${fmt(unitFuel.liters)} L` : 'no fills'} />
                <Cell label={`SPEND ${fuelDays}D`} value={unitFuel ? `SAR ${fmt(unitFuel.sar)}` : '—'} />
                <Cell
                  label="EFFICIENCY"
                  value={unitFuel?.litersPer100Km != null ? `${unitFuel.litersPer100Km} L/100km` : 'odometers unreliable'}
                />
                <Cell label="LOAD" value={selectedUnit.loadKg != null ? `${fmt(selectedUnit.loadKg)} kg` : 'no sensor'} />
                <Cell label="ENGINE HOURS" value={selectedUnit.engineHours ? fmt(selectedUnit.engineHours) : '—'} />
                <Cell label="DRIVER PHONE" value={selectedUnit.driverPhone ?? '—'} />
              </div>
            </div>
          )}
        </section>

        <aside className="ct-rail">
          <div className="ct-railTitle">LIVE EVENTS</div>
          <div className="ct-events">
            {s.events.length === 0 && <div className="ct-empty">nothing yet</div>}
            {s.events.slice(0, 20).map((e) => (
              <div className="ct-ev" key={e.id}>
                <span className={`dot ${e.severity}`} />
                <div>
                  <div className="txt">{e.message}</div>
                  <div className="when">{hhmm(e.occurred_at)}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Trucks with no device. They cannot be mapped, so they live here
              rather than being left off the wall entirely. */}
          {s.untracked.length > 0 && (
            <div className="ct-untracked">
              <div className="ct-railTitle">
                NO TRACKER · {s.untracked.length} OF {s.units.length + s.untracked.length}
              </div>
              <div className="ct-untrackedList">
                {s.untracked.map((v) => {
                  const f = s.fuel?.byUnit[v.code];
                  return (
                    <div className="ct-ev" key={v.code}>
                      <span className="dot" />
                      <div>
                        <div className="txt">
                          {v.plate || v.code}
                          {f && <span className="ct-untrackedFuel">SAR {fmt(f.sar)}</span>}
                        </div>
                        {/* No GPS, but the diesel card still says where it has
                            been and how far it has run. */}
                        <div className="when">
                          {[
                            v.driverName ?? 'no driver',
                            v.lastOdometerKm != null ? `${fmt(v.lastOdometerKm)} km` : null,
                            v.lastFuelAt ? `fuelled ${new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short' }).format(new Date(v.lastFuelAt))}` : 'no fills recorded',
                          ].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {s.alerts.length > 0 && (
            <div className="ct-alertList">
              <div className="ct-railTitle"><AlertTriangle className="h-3.5 w-3.5" /> OPEN ALERTS</div>
              {s.alerts.slice(0, 6).map((a) => (
                <div className="ct-ev" key={a.id}>
                  <span className={`dot ${a.severity}`} />
                  <div><div className="txt">{a.message}</div><div className="when">{hhmm(a.opened_at)}</div></div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </main>

      <footer className="ct-ticker">
        <div className="track">
          <span><b>{s.counts.moving}</b> moving · <b>{s.counts.offline}</b> offline · <b>{fmt(s.fleetKmToday)}</b> km today</span>
          {s.alerts.map((a) => <span key={`a${a.id}`}><b>{a.message}</b></span>)}
          {s.events.slice(0, 12).map((e) => <span key={`e${e.id}`}>{e.message}</span>)}
        </div>
      </footer>
    </div>
  );
}

/** One second of wall clock, without re-rendering the map every tick. */
function useTicking(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function Chip({ label, value, danger, warn, wide }: { label: string; value: number | string; danger?: boolean; warn?: boolean; wide?: boolean }) {
  return (
    <div className={`ct-chip ${wide ? 'wide' : ''} ${danger ? 'danger' : ''} ${warn ? 'warn' : ''}`}>
      <b>{value}</b><span>{label}</span>
    </div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return <div>{label}<b>{value}</b></div>;
}
